import React from 'react';
import { Seo } from '../components/Seo';

export const AccessibilityStatement: React.FC = () => {
  return (
    <>
      <Seo
        title="הצהרת נגישות | RIHAM"
        description="הצהרת הנגישות של אתר RIHAM — קליניקה אסתטית וטבעית של רהאם מנסור."
        path="/accessibility-statement"
      />
    <article
      dir="rtl"
      className="bg-[#F9F7F2] text-[#412018] py-24 md:py-32 px-6 min-h-[100dvh]"
    >
      <div className="container mx-auto max-w-3xl">
        <header className="mb-12 border-b border-[#412018]/15 pb-8">
          <p className="text-xs uppercase tracking-[0.3em] text-[#67392b] mb-3">
            נגישות
          </p>
          <h1 className="font-serif text-4xl md:text-6xl leading-tight m-0">
            הצהרת נגישות
          </h1>
        </header>

        <section aria-labelledby="commitment" className="mb-12">
          <h2 id="commitment" className="font-serif text-2xl md:text-3xl mb-4">
            המחויבות שלנו
          </h2>
          <p className="text-base md:text-lg leading-relaxed text-[#412018]/85">
            [שם העסק] רואה חשיבות עליונה במתן שירות שוויוני, מכבד ונגיש לכלל
            הציבור, לרבות אנשים עם מוגבלות. אנו פועלים להנגיש את האתר בהתאם
            לחוק שוויון זכויות לאנשים עם מוגבלות, התשנ״ח-1998, ותקנותיו,
            ובהתאם לתקן הישראלי <strong>ת״י 5568</strong> לנגישות תכנים
            באינטרנט ברמת <strong>AA</strong>, התואם להמלצות{' '}
            <strong>WCAG 2.1</strong> של ארגון <abbr title="World Wide Web Consortium">W3C</abbr>.
          </p>
        </section>

        <section aria-labelledby="digital" className="mb-12">
          <h2 id="digital" className="font-serif text-2xl md:text-3xl mb-4">
            התאמות נגישות באתר
          </h2>
          <p className="text-base leading-relaxed text-[#412018]/85 mb-4">
            בוצעו באתר התאמות הבאות:
          </p>
          <ul className="list-disc ps-6 space-y-2 text-base text-[#412018]/85">
            <li>ניווט מלא באמצעות מקלדת (Tab / Shift+Tab / Enter / Esc).</li>
            <li>תמיכה בקוראי מסך נפוצים (NVDA, JAWS, VoiceOver, TalkBack).</li>
            <li>שימוש בתגיות סמנטיות (header, nav, main, section, footer).</li>
            <li>טקסט חלופי (alt) לתמונות בעלות משמעות.</li>
            <li>תוויות (label) ותיאורים (aria-describedby) לכל שדות הטופס.</li>
            <li>יחס ניגודיות מינימלי של 4.5:1 לטקסט רגיל.</li>
            <li>אין שימוש בצבע כאמצעי יחיד להעברת מידע.</li>
            <li>פוקוס מקלדת נראה לעין על כל אלמנט אינטראקטיבי.</li>
            <li>תפריט נגישות צף הכולל: ניגודיות גבוהה, גווני אפור, הגדלת
              טקסט, הדגשת קישורים והחלפת גופן לקריא.</li>
            <li>כיבוד הגדרת <code>prefers-reduced-motion</code> של המשתמש.</li>
            <li>אין נגינה אוטומטית של מדיה עם פס קול.</li>
          </ul>
        </section>

        <section aria-labelledby="physical" className="mb-12">
          <h2 id="physical" className="font-serif text-2xl md:text-3xl mb-4">
            הסדרי נגישות פיזיים
          </h2>
          <p className="text-base leading-relaxed text-[#412018]/85">
            [תיאור הסדרי הנגישות הפיזיים בסניף/קליניקה: חניית נכים, רמפות,
            דלתות בעלות רוחב מותאם, שירותי נכים, סימון שילוט נגיש וכו׳.]
          </p>
          <p className="text-base leading-relaxed text-[#412018]/85 mt-3">
            {/* OR */}
            [או — אם השירות מקוון בלבד: ״השירות הניתן באתר זה הוא שירות
            מקוון בלבד, ואין לו סניף פיזי הפתוח לקהל.״]
          </p>
        </section>

        <section aria-labelledby="limitations" className="mb-12">
          <h2 id="limitations" className="font-serif text-2xl md:text-3xl mb-4">
            חריגים והסתייגויות
          </h2>
          <p className="text-base leading-relaxed text-[#412018]/85">
            [פירוט עמודים/רכיבים שטרם הונגשו במלואם, אם קיימים, והתחייבות
            ללוח זמנים להשלמת ההנגשה.]
          </p>
        </section>

        <section aria-labelledby="coordinator" className="mb-12">
          <h2 id="coordinator" className="font-serif text-2xl md:text-3xl mb-4">
            רכז/ת נגישות
          </h2>
          <p className="text-base leading-relaxed text-[#412018]/85 mb-4">
            נתקלת בבעיית נגישות באתר? נשמח לקבל ממך פנייה ולטפל בה בהקדם.
          </p>
          <address className="not-italic text-base text-[#412018] bg-white border border-[#412018]/15 p-5 rounded-md shadow-brand-sm">
            <dl className="space-y-2 m-0">
              <div className="flex gap-2">
                <dt className="font-bold min-w-[6rem]">שם:</dt>
                <dd className="m-0">[שם רכז/ת הנגישות]</dd>
              </div>
              <div className="flex gap-2">
                <dt className="font-bold min-w-[6rem]">טלפון:</dt>
                <dd className="m-0">
                  <a
                    href="tel:[Phone]"
                    className="underline hover:text-[#67392b] focus:outline-none focus:ring-2 focus:ring-[#fbbf25] rounded"
                  >
                    [Phone]
                  </a>
                </dd>
              </div>
              <div className="flex gap-2">
                <dt className="font-bold min-w-[6rem]">דוא״ל:</dt>
                <dd className="m-0">
                  <a
                    href="mailto:[Email]"
                    className="underline hover:text-[#67392b] focus:outline-none focus:ring-2 focus:ring-[#fbbf25] rounded"
                  >
                    [Email]
                  </a>
                </dd>
              </div>
              <div className="flex gap-2">
                <dt className="font-bold min-w-[6rem]">כתובת:</dt>
                <dd className="m-0">[כתובת מלאה]</dd>
              </div>
            </dl>
          </address>
        </section>

        <footer className="border-t border-[#412018]/15 pt-6 text-sm text-[#412018]/60">
          <p className="m-0">
            <strong>תאריך עדכון אחרון של הצהרת הנגישות:</strong>{' '}
            <time dateTime="[YYYY-MM-DD]">[DD/MM/YYYY]</time>
          </p>
        </footer>
      </div>
    </article>
    </>
  );
};

export default AccessibilityStatement;
